/**
 * Component: Responsive Background Image
 *
 * Uses an invisible <picture> element with srcset to change the parent
 * container's background image
 *
 * - Data Attr:   `data-c-responsive-bg`
 * - Styles:      N/A
 * - Usage:       See code below
 *
 * @module components/CResponsiveBG
 * @since https://in-beta.codebasehq.com/projects/on24-support/tickets/637
 */


/*
Example 1 (HTML):
	<div style="background-image: url('mobile-size.jpg')" data-c-responsive-bg>
		<div class="visibility-hidden">
			<picture>
				<source media="(min-width: 1280px)"                        srcset="xl-size.jpg">
				<source media="(min-width: 992px) and (max-width: 1199px)" srcset="lg-size.jpg">
				<source media="(min-width: 768px) and (max-width: 991px)"  srcset="md-size.jpg">
				<source media="(min-width: 576px) and (max-width: 767px)"  srcset="sm-size.jpg">
				<img src="mobile-size.jpg" alt="">
			</picture>
		</div>
	</div>

Example 2 (Fly-generated picture):

	$image    = get_sub_field( 'image' );
	$image_ID = ( $image ) ? $image['ID'] : null;

	if ( $image_ID )
	{
		$picture_sizes = array(
			'desktop'   => array( 'width' => 1920, 'height' => 800 ),
			'tablet-po' => array( 'width' =>  768, 'height' => 600 ), // Max BP: 768
			'mobile-lg' => array( 'width' =>  414, 'height' => 300 ), // Max BP: 414
		);
		$mobile_img = app_fly_get_image_src( $image_ID, array( 414, 300 ) );
		$picture_el = app_fly_get_picture_element_ext( $image_ID, $picture_sizes );
	}
	else
	{
		// Use placeholder (set in the CMS, via Options > Developer)
		$mobile_img = app_fly_get_placeholder_src( 'large', array( 414, 300 ) );
		$picture_el = app_fly_get_placeholder_picture_element_ext( $image_ID, $picture_sizes );
	}
	?>

	<div style="background-image: url('<?= $mobile_img ?>')" data-c-responsive-bg>
		<div class="visibility-hidden">
			<?= $picture_el ?>
		</div>
	</div>
*/


// Dependances
// ============================================================================

import { $ } from '../common/utility/u-jquery-$';
import { IComponentSetup } from './shared/s-component-setup';


// Internal Variables
// ============================================================================

/**
 * Default options, overwritten with JSON on the data attribute
 *
 * @var {Object}
 *
 * @property {bool}  [debugComponent=false]  True to enable extensive logging for each found instance of this component. Errors and warnings are always logged regardless
 */
const defaultOptions = {
	debugComponent: false,
};


// Init
// ============================================================================

/**
 * Core component init
 *
 * @return  {void}
 */
function _componentInit()
{
	const setupOpts = {
		name:        'responsive-bg',
		dataAttr:    'data-c-responsive-bg',
		defaultOpts: defaultOptions,
		Class:       ResponsiveBGComponent, // Name of the class below
		debugSetup:  false, // True to log setup info for this component (use debugComponent for per-instance logs)
	};

	IComponentSetup.setupComponent( setupOpts );
}


// Single Component Class
// ============================================================================

class ResponsiveBGComponent
{
	/**
	 * Example component constructor
	 *
	 * @param   {jQuery}  $container  jQuery container element
	 * @param   {object}  options     Options object
	 *
	 * @return  {void}
	 */
	constructor( $container, options, instanceIndex )
	{
		// Always available to all components
		this.$container    = $container;
		this.options       = options;
		this.instanceIndex = instanceIndex;
	}


	/**
	 * Main init
	 *
	 * Run automatically via setupComponent
	 *
	 * @return {void}
	 */
	_classInit()
	{
		this.init();
	}


	init()
	{
		const $rspBgs = $( '[data-c-responsive-bg]' );

		$rspBgs.each( ( i, element ) =>
		{
			const $el = $( element );

			try
			{
				const img = $el.find( 'img' )[0];

				// Store current src in element's data
				// Updated in main()
				$el.data( 'bgsrc', '' );

				this.validateElements( img, $el );

				// If validation passed, proceed
				this.main( img, $el );
			}
			catch( err )
			{
				console.error( `[ResponsiveBG] ${err.message}`, $el );
				return;
			}
		});
	}

	main( img, $el )
	{
		// Triggered when the browser loads in a new source image
		$( img ).on( 'load', () => this.updateBg( img, $el ) );

		// If image was already loaded, update BG immediately
		if( img.complete )
		{
			this.updateBg( img, $el );
		}
	}

	updateBg( img, $el )
	{
		// Get current src
		let currentSrc = ( typeof img.currentSrc !== 'undefined' ) ? img.currentSrc : img.src;

		if ( $el.data( 'bgsrc' ) !== currentSrc )
		{
			// Image src has changed since we last checked, so update the BG img
			$el.data( 'bgsrc', currentSrc );
			$el.css( 'background-image', `url("${currentSrc}")` );
		}
	}

	/**
	 * Check container's elements to ensure they're valid
	 *
	 * Throws errors if validaiton fails. Errors must be caught in calling function
	 */
	validateElements( img, $el )
	{
		if ( !img )
		{
			throw new Error( 'Responsive BG container must contain an <img> element' );
		}

		if ( img.src === '' )
		{
			throw new Error( 'Responsive BG container contains an image with an empty src attribute' );
		}

		const isPicture  = ( $el[0].tagName === 'PICTURE' );
		const hasPicture = ( $el.find( 'picture' ).length );
		const hasSource  = ( $el.find( 'source' ).length );

		if ( !(isPicture || hasPicture) )
		{
			throw new Error( 'Responsive BG container must contain a <picture> element' );
		}

		if ( !hasSource )
		{
			throw new Error( 'Responsive BG container must contain a <source> element' );
		}
	}
}


// Exports
// ============================================================================

// This can be used in any other file
export const CResponsiveBG = { defaultOptions, _componentInit, ResponsiveBGComponent };


// Auto Init
// ============================================================================

_componentInit();
